<template>
  <div class="w-full max-h-screen bg-background flex flex-col relative">
    <page-header title="Returns" backTo="internal">
      <router-link
          :to="{ name: 'return-create' }"
          class="flex items-center bg-green-200 border hover:bg-green-300 border-green-400 hover:border-green-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
      >
        <svg
            class="stroke-current h-5 w-5 mr-2"
            fill="none"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
        >
          <path d="M12 5v14M5 12h14"/>
        </svg>
        <span>New Return</span>
      </router-link>
    </page-header>
    <div class="p-6 bg-background flex-1 overflow-y-scroll">
      <div class="w-full bg-white p-5 rounded shadow-md relative">
        <div class="flex justify-between items-center mb-4">
          <TabHeaders :tabs="tabs" :selectedTab="currentTab" @tab-selected="onTabSelected"/>
          <div class="flex flex-1 justify-end">
            <input
                type="text"
                class="ml-10 w-full lg:w-1/2 rounded-l bge-input bge-input-spacing"
                placeholder="Search"
                v-model="searchInput"
                @keydown.enter="triggerSearch"
            />
            <button
                @click="triggerSearch"
                class="rounded-r bg-blue-200 hover:bg-blue-300 border border-blue-400 hover:border-blue-500 p-2 hover:shadow active:shadow-inner transition-colors duration-300 ease-in-out"
            >
              <Icon iconType="search"/>
            </button>
          </div>
        </div>
        <DataTableComponent :columns="columns" @row-click="onRowClick">
          <template v-slot:timestamp-slot="{ rowData }">
            {{ formatTimestamp(rowData.created_at) }}
          </template>
          <template v-slot:status-slot="{ rowData }">
              <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wide"
                  :class="rowData.status === 'confirmed'
                    ? 'bg-green-100 text-green-700 border border-green-200'
                    : 'bg-yellow-100 text-yellow-700 border border-yellow-200'"
              >
                {{ formatStatus(rowData.status) }}
              </span>
          </template>

          <template v-slot:custom-actions-slot="{ rowData }">
            <div class="flex justify-end items-center">
              <button
                  v-if="rowData.status !== 'confirmed'"
                  type="button"
                  @click.stop="confirmReturn(rowData)"
                  class="bg-green-200 border hover:bg-blue-300 border-blue-400 hover:border-blue-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
              >
                <Icon iconType="checkbox"/>
              </button>
              <button
                  type="button"
                  @click.stop="editReturn(rowData)"
                  class="ml-2 bg-blue-200 border hover:bg-blue-300 border-blue-400 hover:border-blue-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
              >
                <Icon iconType="edit"/>
              </button>
              <button
                  type="button"
                  @click.stop="deleteReturn(rowData)"
                  class="ml-2 bg-red-200 border hover:bg-red-300 border-red-400 hover:border-red-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
              >
                <Icon iconType="delete"/>
              </button>
            </div>
          </template>
        </DataTableComponent>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import TabHeaders from "@/components/TabHeaders.vue";
import DataTableComponent from "./Components/DataTableComponent.vue";
import Icon from "@/components/Icon.vue";
import { mapActions, mapState } from "vuex";
import notificationMixin from "@/mixins/notificationMixin";

export default {
  mixins: [notificationMixin],
  components: {
    PageHeader,
    TabHeaders,
    DataTableComponent,
    Icon,
  },
  data() {
    return {
      tabs: [
        { id: 'open', label: 'Open', iconType: 'not-confirmed' },
        { id: 'confirmed', label: 'Confirmed', iconType: 'confirmed' },
        { id: 'search', label: 'Search', iconType: 'search' },
      ],
      columns: [
        { field: 'return_no', header: 'Return No', sortable: true, custom: false, width: '10%' },
        { field: 'created_at', header: 'Date / Time', sortable: true, custom: true, slotName: 'timestamp-slot', width: '13%' },
        { field: 'requested_by', header: 'Requested By', sortable: true, custom: false, width: '13%' },
        { field: 'agent_contact.name', header: 'Agent', sortable: true, custom: false, width: '15%' },
        { field: 'customer_contact.name', header: 'Customer', sortable: true, custom: false, width: '15%' },
        { field: 'status', header: 'Status', sortable: true, custom: true, slotName: 'status-slot', width: '10%' },
        { field: 'actions', header: '', sortable: false, custom: true, slotName: 'custom-actions-slot', width: '10%' },
      ],
    };
  },
  computed: {
    ...mapState('returns', ['currentTab']),
    searchInput: {
      get() {
        return this.$store.state.returns.tabs.search.searchText;
      },
      set(value) {
        this.$store.commit('returns/UPDATE_SEARCH_TEXT', { tab: 'search', searchText: value });
      },
    },
  },
  mounted() {
    this.fetchTableData();
  },
  methods: {
    ...mapActions('returns', ['fetchTableData', 'updateSearchCriteria', 'updateTab']),
    formatTimestamp(unix) {
      return this.$moment.unix(unix).format('DD-MM-YYYY HH:mm');
    },
    onTabSelected(newTab) {
      this.setTab(newTab);
    },
    setTab(newTab) {
      this.$store.dispatch('returns/updateTab', newTab);
    },
    triggerSearch() {
      this.$store.commit('returns/UPDATE_CURRENT_PAGE', { tab: 'search', page: 1 });
      this.setTab('search');
    },
    onRowClick(event) {
      this.editReturn(event.data);
    },
    async confirmReturn(row) {
      await this.confirmReturnAction(row);
    },
    async confirmReturnAction(ret) {
      await this.confirmAction({
        message: `Are you sure you want to confirm the return with number '${ret.return_no}'?`,
        accept: () => this.executeConfirm(ret),
      });
    },
    async executeConfirm(ret) {
      try {
        const u = (this.$store && this.$store.state && this.$store.state.user) ? this.$store.state.user : null;

        const confirmedBy = {
          name: u ? ((u.first_name || "") + " " + (u.last_name || "")).trim() : "",
          staff_id: u ? (u.staff_id || null) : null,
        };

        const updates = {
          status: "confirmed",
          confirmed_by: confirmedBy,
        };

        await this.ReturnService.updateReturn(ret.return_id, updates);
        await this.$store.dispatch("returns/fetchTableData");
        this.notifySuccess(`Return '${ret.return_no}' confirmed.`);
      } catch (e) {
        this.notifyError("Failed to confirm return.");
      }
    },
    editReturn(row) {
      this.$router.push({ name: 'return-edit', params: { returnId: row.return_id } });
    },
    async deleteReturn(row) {
      const confirmed = await this.confirmAction({ message: `Are you sure you want to delete return '${row.return_no}'?` });
      if (!confirmed) return;
      try {
        await this.ReturnService.deleteReturn(row.return_id);
        await this.$store.dispatch('returns/fetchTableData');
        this.notifySuccess('Return deleted successfully.');
      } catch (e) {
        this.notifyError('Failed to delete return.');
      }
    },
    formatStatus(status) {
      const s = (status || '').toString().trim().toLowerCase();
      if (s === 'confirmed') return 'Confirmed';
      if (s === 'open') return 'Open';
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
    },

  },
};
</script>